/**
 * AppShell — the global navigation chrome (rebuilt 2026-08, counter revamp).
 *
 * WHAT WAS WRONG. At 1440px the header read: wordmark, four text tabs, a
 * search field, a SEPARATE solid teal search button, a circular theme disc,
 * then three full-size language buttons spelling "Русский" "Кыргызча"
 * "English", then an avatar. Nine controls in a row with one `flex-1` spacer
 * between them and no other structure — the language cluster alone took about
 * a quarter of the bar to restate two options the user had already declined.
 * On mobile it was the opposite failure: a lone search glyph floating in the
 * middle of an otherwise empty bar, with no brand and no language control at
 * all.
 *
 * WHAT IT IS NOW. Three declared zones on one drawn rule:
 *
 *   [ wordmark · primary tabs ] ······ [ search ] │ [ language · theme · you ]
 *
 * The zones are separated by real space and, on desktop, by a drawn vertical
 * rule — the direction's "visible armature" raise, applied to the chrome. The
 * bottom edge is `--g-rule`, not the washed-out component hairline, and the
 * active tab is marked by THICKENING that rule under it: a 2px shared-layout
 * element that slides along the bottom of the bar as the route changes. That
 * replaces the soft rounded pill, which was the same generic tab treatment
 * every generated app ships.
 *
 * The language control collapsed from ~330px to ~60px (see LanguageSwitcher),
 * which is what bought the breathing room and what lets the switcher exist on
 * mobile at all — it is now present at every width, alongside the wordmark
 * that the mobile bar never had.
 *
 * Mobile bottom bar: the same 4 destinations, the same sliding shared-layout
 * indicator (`nav-mobile-indicator`), but the indicator is now the same 2px
 * rule as the desktop one, drawn along the tab's TOP edge so it reads as a
 * continuation of the bar's own rule rather than as a floating chip. The
 * icon/label rhythm is tightened and the safe-area inset is honoured with a
 * floor so the bar never sits flush against a home indicator.
 *
 * Route transitions are directional (`routeTransition`): moving right along
 * the tab order brings the new screen in from the right. Deep links and
 * routes outside the tab set degrade to the plain cross-fade. Everything is
 * gated through `useGardenMotion`.
 *
 * Layout is unchanged: a full-viewport flex column — header (shrink-0), the
 * scrollable routed region (`flex-1 overflow-y-auto`), the mobile tab bar
 * (shrink-0). The header keeps its `h-14` / `md:h-16` heights because
 * `--g-toast-offset-top` in tailwind.css is derived from them.
 *
 * DESKTOP HEADER SWAP (2026-08, assistant-surface Task 7). Everything above
 * describes the header this file has always rendered — it still renders it,
 * unchanged, as the fallback branch. `AppShell` now also mounts the imported
 * design's `DesktopHeader` (spec §4.1) instead, but ONLY when BOTH
 * `useDesignWorld()` resolves light (Ruling R17: the imported chrome ships
 * no dark ramp) AND the viewport clears the design's own 1200px frame
 * (`useDesignHeaderViewport` below; narrower than this file's `md` 768px).
 * Fail either check and this header renders exactly as documented above,
 * `LayoutGroup id="nav-desktop"` and all. The mobile bottom bar is untouched
 * either way.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import {
  BookOpen,
  MessageCircle,
  MoreHorizontal,
  Store,
  type LucideIcon,
} from "lucide-react";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { pressClass } from "@/components/motion";
import { AccountMenu } from "@/app/AccountMenu";
import { DesktopHeader } from "@/app/shell/DesktopHeader";
import { useDesignWorld } from "@/app/shell/useDesignWorld";
import { HeaderSearchBar } from "@/features/marketplace/HeaderSearchBar";
import { iconProps } from "@/lib/icons";
import { indicatorTransition, routeTransition } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";

export interface NavItem {
  id: string;
  to: string;
  Icon: LucideIcon;
  labelKey: string;
  /** false until the destination screen exists → rendered as a disabled tab. */
  enabled: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  // The captured-renovation "project" dashboard tab was removed 2026-07-06
  // (feature hidden product-wide). The assistant is now the consumer home
  // (DOOR_DEFAULT.consumer = "/chat") and the first tab.
  { id: "assistant", to: "/chat", Icon: MessageCircle, labelKey: "shell.assistant", enabled: true },
  { id: "shops", to: "/shops", Icon: Store, labelKey: "shell.shops", enabled: true },
  { id: "library", to: "/library", Icon: BookOpen, labelKey: "shell.library", enabled: true },
  { id: "more", to: "/settings", Icon: MoreHorizontal, labelKey: "shell.more", enabled: true },
];

/**
 * Hidden from the navigation for now (owner, 2026-10-09): neither the phone
 * bottom bar nor the desktop header draws «Магазины» or «База». UI only —
 * the `/shops` and `/library` routes stay (App.tsx), and so does every
 * in-content link to them (answer links, «Смотреть все →», the landing, the
 * seller pages). To bring a tab back, delete its id here; nothing else
 * changes. Overlaps SCRUM-272 ("bottom nav without «Магазины»/«База»").
 */
export const HIDDEN_NAV_IDS: ReadonlySet<string> = new Set(["shops", "library"]);

/** The tabs the navigation draws: `NAV_ITEMS` less `HIDDEN_NAV_IDS`. */
export const VISIBLE_NAV_ITEMS: readonly NavItem[] = NAV_ITEMS.filter(
  (item) => !HIDDEN_NAV_IDS.has(item.id),
);

/**
 * The AppShell per-route cross-fade's identity key for a given pathname.
 * Normally the raw pathname (a genuine screen change should remount + fade).
 * SCRUM-147: the Shops finder's seller detail is a BottomSheet overlay on
 * the SAME screen (SCRUM-140 deep-links it via /shops/sellers/:id so it's
 * shareable), not a distinct screen — but its pathname differs from the bare
 * /shops list, which would otherwise make the cross-fade treat opening/
 * closing a seller as a screen change (a real, reproduced bug: every seller
 * click destroyed and recreated the 2GIS map instance, with a blank-page
 * flash while the old screen exited and the new one entered). Normalized
 * back to /shops here so it behaves like switching chat threads (which
 * never changes the pathname) — no remount. Every other route keeps its
 * full-pathname identity. Exported (pure, no component deps) so this exact
 * mapping is unit-testable without exercising Framer Motion's real
 * AnimatePresence timing, which JSDOM can't reliably drive.
 */
export function crossfadeKeyFor(pathname: string): string {
  return pathname.startsWith("/shops/sellers/") ? "/shops" : pathname;
}

/**
 * Where a tab points from `pathname`, and whether it must match exactly.
 * Guest chat (SCRUM-275): `/` IS the assistant now — HomeRoute renders the
 * chat there for guests and sends members on to /chat — so at `/` the
 * assistant tab points at `/` with `end` (active there, and only there);
 * everywhere else it keeps pointing at /chat, which ChatEntryRoute forwards to
 * `/` for a guest. Shared with DesktopHeader.
 */
export function navTarget(item: NavItem, pathname: string): { to: string; end: boolean } {
  return item.id === "assistant" && pathname === "/"
    ? { to: "/", end: true }
    : { to: item.to, end: false };
}

/**
 * Which tab a pathname belongs to, or -1 for a route outside the tab set
 * (product detail, materials, the account screens). Drives the directional
 * route transition: with a fixed tab order, "which way did I move" is
 * information the nav already carries, so the screen change can carry it too.
 */
function navIndexFor(pathname: string): number {
  // `/` is the assistant for guests (SCRUM-275).
  if (pathname === "/") return NAV_ITEMS.findIndex((item) => item.id === "assistant");
  return NAV_ITEMS.findIndex((item) => pathname.startsWith(item.to));
}

/**
 * The imported design's own min-width frame (spec §4.1's 64px header lives
 * inside the `min-width:1200px` assistant frame; §6 states the gap plainly:
 * "1200px > viewport ≥ 768px: Today's layout. The design's frame requires
 * 1200px; `useIsDesktop` breaks at 768px, so the frame needs its own
 * threshold"). This is a SEPARATE query from `useIsDesktop` (768px,
 * `features/chat/useIsDesktop.ts`) — that hook drives drawer/sheet layout
 * elsewhere and stays as-is; the design header needs its own, wider gate.
 * Below 1200px the incumbent header renders even in light theme, because
 * the design supplies no composition for that band (mirrors `useIsDesktop`'s
 * own matchMedia-listener shape).
 */
const DESIGN_HEADER_QUERY = "(min-width: 1200px)";

function useDesignHeaderViewport(): boolean {
  const [wide, setWide] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(DESIGN_HEADER_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mql = window.matchMedia(DESIGN_HEADER_QUERY);
    const onChange = (event: MediaQueryListEvent): void => setWide(event.matches);
    setWide(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return wide;
}

export function AppShell(): ReactElement {
  const { t } = useTranslation("common");
  const { reduced } = useGardenMotion();
  const location = useLocation();
  const crossfadeKey = crossfadeKeyFor(location.pathname);
  const indicator = indicatorTransition(reduced);
  // R17 (design surface spec §"CRITICAL CONSTRAINT"): the imported chrome is
  // light-only, so it mounts only when BOTH the resolved theme is light AND
  // the viewport clears the design's own 1200px frame. Either condition
  // failing keeps the incumbent header below, unchanged — dark theme never
  // sees the cream `--gd-ground` bar, and 768-1199px never sees a squeezed
  // version of a header the design never drew at that width. Both hooks are
  // called unconditionally (never `a() && b()`, which would short-circuit
  // the second call and break the Rules of Hooks) and combined afterwards.
  const isDesignWorld = useDesignWorld();
  const isDesignHeaderViewport = useDesignHeaderViewport();
  const showDesktopHeader = isDesignWorld && isDesignHeaderViewport;

  // Direction is computed during render (the value the ENTERING screen needs)
  // and committed afterwards, so a tab change reads -1/+1 exactly once.
  const navIndex = navIndexFor(location.pathname);
  const previousIndex = useRef(navIndex);
  const direction =
    navIndex === -1 || previousIndex.current === -1
      ? 0
      : Math.sign(navIndex - previousIndex.current);
  useEffect(() => {
    previousIndex.current = navIndex;
  }, [navIndex]);

  const screenMotion = routeTransition(direction, reduced);

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Keyboard users land on the header's controls before the screen they
          asked for; with the tools cluster this is now a dozen stops. */}
      <a
        href="#app-shell-main"
        className="sr-only rounded-[10px] bg-primary px-4 py-2 text-sm font-semibold text-[color:var(--g-on-primary)] focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50"
      >
        {t("shell.skipToContent")}
      </a>

      {/*
       * R17: the imported design's chrome is light-only (it ships one
       * palette, no dark ramp — see `useDesignWorld`'s docstring) and its
       * own frame requires 1200px (`useDesignHeaderViewport` above). Outside
       * that light+wide window the incumbent header below renders exactly as
       * it always has — dark theme, and 768-1199px in ANY theme, both fall
       * through to it. The two branches are mutually exclusive, so their
       * shared child testids (`nav-desktop-*`, the account menu's) never
       * collide; only one is ever mounted.
       */}
      {showDesktopHeader ? (
        <DesktopHeader />
      ) : (
      <header
        role="banner"
        data-testid="app-shell-header"
        className="z-40 shrink-0 border-b border-[color:var(--g-rule)] bg-background"
      >
        <div className="mx-auto flex h-14 w-full max-w-screen-xl items-stretch gap-2 px-3 md:h-16 md:gap-4 md:px-6">
          {/* ── Zone 1: identity + destinations ─────────────────────────── */}
          {/* The wordmark is present at every width now. The mobile bar used
              to open with nothing, which is why its lone search glyph read as
              floating. It is the «GardEn» logotype, so it reads the logotype
              face (`font-wordmark`, Unbounded), not the display face: the
              owner kept the logotype as it was when Onest became the display
              face (2026-10-09). A plain `Link`, not a `NavLink`: `to="/"`
              without `end` matches EVERY path, so as a NavLink the wordmark
              carried `aria-current="page"` on every screen, competing with the
              real active tab for the same announcement. */}
          <Link
            to="/"
            data-testid="app-shell-wordmark"
            className="flex shrink-0 items-center rounded-[10px] px-1 font-wordmark text-[0.9375rem] font-bold tracking-[-0.04em] text-primary transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background md:text-[1.0625rem]"
          >
            {t("appName")}
          </Link>

          <LayoutGroup id="nav-desktop">
            <nav
              aria-label={t("shell.primary")}
              className="hidden items-stretch md:ml-2 md:flex"
            >
              {VISIBLE_NAV_ITEMS.map((item) => {
                const Icon = item.Icon;
                return item.enabled ? (
                  <NavLink
                    key={item.id}
                    to={navTarget(item, location.pathname).to}
                    end={navTarget(item, location.pathname).end}
                    data-testid={`nav-desktop-${item.id}`}
                    className={({ isActive }) =>
                      [
                        "relative flex items-center gap-2 px-3.5 text-sm transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                        // The active label is the brand as TEXT: identical to
                        // `text-primary` in light, the lightened ink in dark,
                        // where `--g-primary` on the ground is 2.5:1 (D2).
                        isActive
                          ? "font-semibold text-[color:var(--g-primary-text)]"
                          : "font-medium text-[color:var(--g-ink-2)] hover:bg-[color:var(--g-sunken)] hover:text-foreground",
                      ].join(" ")
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          {...iconProps("sm")}
                          className={
                            isActive
                              ? "text-[color:var(--g-primary-text)]"
                              : "text-[color:var(--g-ink-3)]"
                          }
                        />
                        <span className="whitespace-nowrap">{t(item.labelKey)}</span>
                        {/* The armature, marked: the header's own bottom rule
                            thickens under the active tab and SLIDES between
                            tabs (shared layout) instead of a pill fading in. */}
                        {isActive && (
                          <motion.span
                            aria-hidden="true"
                            layoutId="nav-desktop-active"
                            transition={indicator}
                            className="absolute inset-x-0 -bottom-px h-0.5 bg-primary"
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                ) : (
                  <span
                    key={item.id}
                    aria-disabled="true"
                    title={t("shell.soon")}
                    className="flex cursor-not-allowed items-center gap-2 px-3.5 text-sm font-medium text-[color:var(--g-ink-3)]"
                  >
                    <Icon {...iconProps("sm")} />
                    <span className="whitespace-nowrap">{t(item.labelKey)}</span>
                  </span>
                );
              })}
            </nav>
          </LayoutGroup>

          {/* ── Zone 2: search ──────────────────────────────────────────── */}
          {/* `ml-auto` pushes both remaining zones right as one block, so the
              gap between the tabs and the search is real space rather than a
              stretched spacer that moved every control to an edge. Below lg
              (1024px) this is a single 44px glyph, so it sits WITH the tools
              instead of stranded mid-bar — and a 768px tablet's bar fits. */}
          <div className="ml-auto flex shrink-0 items-center lg:w-full lg:max-w-[19rem] lg:shrink">
            <HeaderSearchBar />
          </div>

          {/* Drawn zone divider — the header's own column rule. */}
          <span
            aria-hidden="true"
            className="hidden w-px self-center bg-[color:var(--g-rule)] md:block md:h-6"
          />

          {/* ── Zone 3: the tools ───────────────────────────────────────── */}
          <div className="flex shrink-0 items-center gap-1.5 md:gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <AccountMenu />
          </div>
        </div>
      </header>
      )}

      {/* Routed content — scrolls between the top chrome and the bottom nav.
          Keyed on `crossfadeKey` (normally the raw pathname). `mode="wait"`
          swaps one screen at a time (no overlap / double scrollbar);
          `initial={false}` skips the transition on first paint so a cold load
          and the tests render content synchronously. The wrapper is `h-full`
          so screens that rely on filling `<main>` (e.g. chat) keep their
          height. `overflow-x-clip` contains the directional offset so it can
          never produce a horizontal scrollbar. Intra-screen navigation that
          doesn't change `crossfadeKey` does NOT remount — e.g. switching chat
          threads (never changes the pathname) or opening/closing the Shops
          seller-detail sheet (SCRUM-147, normalized above). `tabIndex={-1}`
          makes this the skip link's landing spot and keeps the scrollable
          region keyboard-reachable. */}
      <main
        id="app-shell-main"
        tabIndex={-1}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-clip focus-visible:outline-none"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={crossfadeKey}
            data-testid="app-shell-screen"
            className="h-full"
            {...screenMotion}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Mobile bottom tab bar. The active tab's marker is the same 2px rule
          as the desktop one, drawn along the tab's top edge so it reads as the
          bar's own rule thickening rather than as a floating chip; it SLIDES
          between tabs (shared layout) and jumps under reduced motion. The
          safe-area inset carries a floor so the bar never sits flush against a
          home indicator. `pressClass("navTab")` adds the tap-scale, gated via
          `motion-reduce:`. */}
      <LayoutGroup id="nav-mobile">
        <nav
          aria-label={t("shell.primary")}
          data-testid="app-shell-bottom-nav"
          className="flex shrink-0 items-stretch justify-around border-t border-[color:var(--g-rule)] bg-background pb-[max(0.25rem,env(safe-area-inset-bottom))] md:hidden"
        >
          {VISIBLE_NAV_ITEMS.map((item) => {
            const Icon = item.Icon;
            return item.enabled ? (
              <NavLink
                key={item.id}
                to={navTarget(item, location.pathname).to}
                end={navTarget(item, location.pathname).end}
                data-testid={`nav-mobile-${item.id}`}
                className={({ isActive }) =>
                  [
                    "relative flex min-w-14 flex-1 flex-col items-center justify-center gap-1 px-1 pb-1.5 pt-2.5 transition-colors",
                    pressClass("navTab"),
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                    isActive ? "text-primary" : "text-[color:var(--g-ink-2)]",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        aria-hidden="true"
                        data-testid="nav-mobile-indicator"
                        layoutId="nav-mobile-active"
                        transition={indicator}
                        className="absolute inset-x-0 top-0 h-0.5 bg-primary"
                      />
                    )}
                    <Icon {...iconProps("lg")} />
                    <span
                      className={`text-[0.6875rem] leading-none ${isActive ? "font-semibold" : "font-medium"}`}
                    >
                      {t(item.labelKey)}
                    </span>
                  </>
                )}
              </NavLink>
            ) : (
              <span
                key={item.id}
                aria-disabled="true"
                className="flex min-w-14 flex-1 cursor-not-allowed flex-col items-center justify-center gap-1 px-1 pb-1.5 pt-2.5 text-[color:var(--g-ink-3)]"
              >
                <Icon {...iconProps("lg")} />
                <span className="text-[0.6875rem] font-medium leading-none">
                  {t(item.labelKey)}
                </span>
              </span>
            );
          })}
        </nav>
      </LayoutGroup>
    </div>
  );
}

export default AppShell;
